import React from 'react'

import { Box, Chip, Typography, makeStyles, Theme } from '@material-ui/core'
import {
    Event as EventIcon,
    FormatPaint as ServiceOrderIcon,
    Assignment as ActionItemIcon,
    AssignmentInd as ServiceRequestIcon,
    AssignmentTurnedIn as ProjectIcon,
    Alarm as DeadlineIcon,
    PersonAdd as MoveInIcon,
    EmojiPeople as MoveOutIcon,
    DirectionsWalk as TourIcon,
    ChevronRight as ChevronRightIcon,
} from '@material-ui/icons'

import { ReactComponent as ClipboardTickSBIcon } from '../../assets/icons/clipboard-tick.svg'

import {
    ACTIVITY_TYPE_COLOR,
    ACTIVITY_TYPE_LABEL,
    ActivityType,
    DailyActivityItem as DailyActivityItemType,
} from './types'

const BORDER = '#e6e9ed'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        marginBottom: theme.spacing(0.75),
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            boxShadow: '0 3px 12px rgba(0,0,0,0.07)',
            transform: 'translateY(-1px)',
        },
        '&:active': {
            transform: 'translateY(0px)',
            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        },
    },
    accent: {
        width: 3,
        alignSelf: 'stretch',
        borderRadius: 4,
        flexShrink: 0,
        marginLeft: -4,
        marginRight: 4,
    },
    typeIcon: {
        width: 34,
        height: 34,
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    info: {
        flex: 1,
        minWidth: 0,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    title: {
        fontWeight: 600,
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: '#1f2937',
    },
    meta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        marginTop: 3,
        flexWrap: 'wrap',
    },
    metaItem: {
        fontSize: '0.72rem',
        color: '#6b7280',
    },
    metaDot: {
        width: 3,
        height: 3,
        borderRadius: '50%',
        backgroundColor: '#d1d5db',
        flexShrink: 0,
    },
    statusChip: {
        height: 18,
        fontSize: '0.62rem',
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: '0.02em',
    },
    chevron: {
        color: '#d1d5db',
        flexShrink: 0,
        transition: 'color 0.15s, transform 0.15s',
        '$card:hover &': {
            color: theme.palette.primary.main,
            transform: 'translateX(2px)',
        },
    },
}))

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
    Draft: { bg: '#f3f4f6', color: '#6b7280' },
    Open: { bg: '#fef3c7', color: '#d97706' },
    Published: { bg: '#ecfdf5', color: '#059669' },
    Complete: { bg: '#ecfdf5', color: '#059669' },
}

const TYPE_ICONS: Record<ActivityType, React.ReactNode> = {
    COMMUNITY_EVENT: <EventIcon style={{ fontSize: 17 }} />,
    SERVICE_ORDER: <ServiceOrderIcon style={{ fontSize: 17 }} />,
    ACTION_ITEM: <ActionItemIcon style={{ fontSize: 17 }} />,
    SERVICE_REQUEST: <ServiceRequestIcon style={{ fontSize: 17 }} />,
    PROJECT: <ProjectIcon style={{ fontSize: 17 }} />,
    INSPECTION: <ClipboardTickSBIcon style={{ width: 17, height: 17 }} />,
    TOUR: <TourIcon style={{ fontSize: 17 }} />,
    DEADLINE: <DeadlineIcon style={{ fontSize: 17 }} />,
    MOVE_IN: <MoveInIcon style={{ fontSize: 17 }} />,
    MOVE_OUT: <MoveOutIcon style={{ fontSize: 17 }} />,
}

interface DailyActivityItemProps {
    item: DailyActivityItemType
    onClick: () => void
}

export const DailyActivityItemRow: React.FC<DailyActivityItemProps> = ({
    item,
    onClick,
}) => {
    const classes = useStyles()
    const color = item.color || ACTIVITY_TYPE_COLOR[item.type]
    const statusStyle = item.status
        ? STATUS_STYLES[item.status] ?? { bg: '#f3f4f6', color: '#6b7280' }
        : null

    const metaParts = [
        item.type === 'COMMUNITY_EVENT' ? item.time : undefined,
        item.subtitle,
        ACTIVITY_TYPE_LABEL[item.type],
    ].filter(Boolean) as string[]

    return (
        <Box className={classes.card} onClick={onClick}>
            <Box
                className={classes.accent}
                style={{ backgroundColor: color }}
            />
            <Box
                className={classes.typeIcon}
                style={{
                    backgroundColor: `${color}14`,
                    color,
                }}
            >
                {TYPE_ICONS[item.type]}
            </Box>
            <Box className={classes.info}>
                <Box className={classes.titleRow}>
                    <Typography className={classes.title}>
                        {item.title}
                    </Typography>
                    {item.status && statusStyle && (
                        <Chip
                            size="small"
                            label={item.status}
                            className={classes.statusChip}
                            style={{
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.color,
                            }}
                        />
                    )}
                </Box>
                {metaParts.length > 0 && (
                    <Box className={classes.meta}>
                        {metaParts.map((part, idx) => (
                            <React.Fragment key={`${part}-${idx}`}>
                                {idx > 0 && (
                                    <span className={classes.metaDot} />
                                )}
                                <span className={classes.metaItem}>{part}</span>
                            </React.Fragment>
                        ))}
                    </Box>
                )}
            </Box>
            <ChevronRightIcon
                className={classes.chevron}
                style={{ fontSize: 18 }}
            />
        </Box>
    )
}
